import { ExtendedAddressPreferences, TransactionParams } from "@toruslabs/ethereum-controllers";
import { storageAvailable } from "@toruslabs/openlogin-utils";
import { AxiosError } from "axios";
import { BrowserProvider, Contract, ethers } from "ethers";
import Cookies from "js-cookie";
import { defineStore } from "pinia";
import { ref } from "vue";
import { useRoute } from "vue-router";

import { config } from "../config";
import MocaAPIHandler from "../handlers/MocaID/apiHandler";
import { EMAIL_CONSENT_SESSION_KEY, MOCA_ID_JWT_KEY, MOCA_ID_PARTNER_JWT_KEY } from "../utils/enums";
import { realmIdAbi } from "../utils/realmIdAbi";
import { useAuthStore } from "./authStore";
import { useControllerStore } from "./controllerStore";

type LeftSide = "WhatIsMoca" | "WhyMoca" | "ExploreMocaverse";
type RightSide =
  | "CreateWallet"
  | "ClaimMocaID"
  | "ClaimMocaIDSuccess"
  | "ClaimQuotaFull"
  | "CheckInviteCode"
  | "CheckMoca"
  | "LinkPartner"
  | "ChooseClaimMethod"
  | "ErrorMessage";

export const useClaimStore = defineStore("claim", () => {
  const route = useRoute();
  const authStore = useAuthStore();
  const useController = useControllerStore();

  let token = route.query.token as string;
  let partnerJwt = route.query.partner as string;
  const action = route.query.action as string;

  const leftSide = ref<LeftSide>("WhatIsMoca");
  const rightSide = ref<RightSide>("CreateWallet");
  const isLoading = ref(false);
  const errorText = ref("");
  const emailConsent = ref(true);
  const inviteCode = ref("");
  const mocaId = ref("");
  const partner = ref(null);
  const userInPartner = ref(null);
  const user = ref(null);
  const mocaIdToken = ref("");
  const mocaNFTAddress = ref("");
  const mocaSignature = ref("");

  async function checkEligibility(): Promise<void> {
    try {
      if (storageAvailable("sessionStorage")) {
        emailConsent.value = sessionStorage.getItem(EMAIL_CONSENT_SESSION_KEY) === "true";
        token = token || sessionStorage.getItem(MOCA_ID_JWT_KEY);
        partnerJwt = partnerJwt || sessionStorage.getItem(MOCA_ID_PARTNER_JWT_KEY);
      }

      const preferencesControllerState = useController.torusState.PreferencesControllerState;
      const address = preferencesControllerState.selectedAddress;
      const walletToken = preferencesControllerState.identities[address].jwtToken;
      const data = await MocaAPIHandler.checkEligibility({ partnerJwt, web3AuthJwt: walletToken, jwtToken: token });
      const { signupState } = data.user;
      user.value = data.user;
      if (signupState === "signedup_email") {
        if (data.partner) {
          const isValid = checkPartnerQuota(data.partner);
          if (!isValid) toPartnerQuotaIsFullView();
          else toClaimMocaIDView();
          return;
        }
        if (action === "code") {
          toUseInviteCodeToClaimView();
          return;
        }
        if (action === "nft") {
          toUseNftToClaimView();
          return;
        }
        toSelectClaimActionView();
      } else if (signupState === "eligible_for_moca_id") {
        toClaimMocaIDView();
      } else if (signupState === "minting") {
        toClaimMocaIDView();
      } else if (signupState === "complete") {
        await mocaLogin();
        if (data.partner) {
          partner.value = data.partner;
          userInPartner.value = JSON.parse(atob(partnerJwt.split(".")[1]));
          toLinkPartnerView();
          return;
        }
        toClaimMocaIDSuccessView();
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response.status === 400) {
          errorText.value = error.response.data.message;
        }
      }
      authStore.logout();
      reset();
    }
  }

  async function verifyInviteCode({ code }: { code: string }): Promise<boolean> {
    try {
      await MocaAPIHandler.verifyInviteCode({ referralCode: code });
      inviteCode.value = code;
      toClaimMocaIDView();
      return true;
    } catch (error) {
      return false;
    }
  }

  async function verifyContainEligibleNFT({ address }: { address: string }): Promise<boolean> {
    try {
      const data = await MocaAPIHandler.containEligibleMoca({ walletAddress: address });
      if (data.total > 0) {
        toClaimMocaIDView();
        return true;
      }
      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  async function reserveName(name: string): Promise<void> {
    try {
      const preferencesControllerState = useController.torusState.PreferencesControllerState;
      const address = preferencesControllerState.selectedAddress;
      const smartContractWalletAddress = useController.contractWalletAddress;
      const walletToken = (preferencesControllerState.identities[address] as ExtendedAddressPreferences).jwtToken;
      const data = await MocaAPIHandler.reserveName({
        reserveNameRequest: {
          name,
          toWallet: smartContractWalletAddress,
          partnerJwt,
          web3AuthJwt: walletToken,
          emailConsentAccepted: emailConsent.value,
          referralCode: inviteCode.value,
          walletAddress: mocaNFTAddress.value,
          signature: mocaSignature.value,
        },
        mocaAuthToken: token,
      });
      await signClaimMocaIdTransaction(data);
      await mocaLogin();
      toClaimMocaIDSuccessView();
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response.status === 400) {
          errorText.value = error.response.data.message;
          return;
        }
      }
      errorText.value = "Please try again later.";
    }
  }

  async function signClaimMocaIdTransaction(data: any) {
    // encode abi
    const etherProvider = new BrowserProvider(useController.provider);
    const contract = new Contract(data.contract, realmIdAbi, etherProvider);
    // encode parent node
    const nodeHash = ethers.keccak256(ethers.toUtf8Bytes(data.parentNode));
    // @ts-ignore comment
    const parentNode = ethers.solidityPackedKeccak256(["bytes32", "bytes32"], [ethers.encodeBytes32String(0), nodeHash]);
    const encodedData = contract.interface.encodeFunctionData("register", [
      data.name, // mocaId name
      parentNode, // parent node address
      data.toWallet, // wallet address
      data.preData,
    ]);
    const txParams = {
      from: data.toWallet,
      to: data.contract,
      value: "0x0",
      data: encodedData,
    };
    // @ts-ignore comment
    const txHash = await useController.provider.request<[Partial<TransactionParams>], string>({
      method: "eth_sendTransaction",
      params: [txParams],
    });
    await completeMint(data.transactionId, txHash);
  }

  async function completeMint(transactionId: string, txHash: string): Promise<void> {
    const preferencesControllerState = useController.torusState.PreferencesControllerState;
    const address = preferencesControllerState.selectedAddress;
    const walletToken = (preferencesControllerState.identities[address] as ExtendedAddressPreferences).jwtToken;
    await MocaAPIHandler.completeMint({
      partnerJwt,
      web3AuthJwt: walletToken,
      transactionId,
      transactionReceipt: txHash,
    });
  }

  async function mocaLogin(): Promise<void> {
    try {
      const preferencesControllerState = useController.torusState.PreferencesControllerState;
      const address = preferencesControllerState.selectedAddress;
      const walletToken = preferencesControllerState.identities[address].jwtToken;
      const data = await MocaAPIHandler.mocaLogin({
        web3AuthJwt: walletToken,
      });
      token = data.accessToken;
      const userData = await MocaAPIHandler.getCurrentUser({ authToken: token });
      user.value = userData;
      // set cookie
      Cookies.set("web3auth_jwt", walletToken, {
        expires: 1,
        domain: ".mocaverse.xyz",
        secure: true,
        sameSite: "strict",
      });
    } catch (error) {
      console.log("mocaLogin error:", error);
    }
  }

  async function addPartnerDetails() {
    try {
      const preferencesControllerState = useController.torusState.PreferencesControllerState;
      const address = preferencesControllerState.selectedAddress;
      const { jwtToken } = preferencesControllerState.identities[address] as ExtendedAddressPreferences;
      await MocaAPIHandler.addPartnerDetails({ partnerJwt, web3AuthJwt: jwtToken, mocaIdJwt: mocaIdToken.value });
      toClaimMocaIDSuccessView();
    } catch (error) {
      console.log("addPartnerDetails error:", error);
    }
  }

  function checkPartnerQuota(partnerData: any): boolean {
    partner.value = partnerData;
    userInPartner.value = atob(partnerJwt.split(".")[1]);
    if (partnerData.referralCodes) {
      return partnerData.referralCodes.length > 0 && partnerData.referralCodes[0].isValid;
    }
    return false;
  }

  // async function getPartnerInfo(): Promise<void> {
  //   try {
  //     const data = await MocaAPIHandler.getPartnerInfo({ partnerToken: partnerJwt });
  //     partner.value = data;
  //   } catch (error) {
  //     console.log("getPartnerInfo error:", error);
  //   }
  // }

  function chooseClaimMethod(action: string): void {
    if (action === "code") {
      toUseInviteCodeToClaimView();
    }
    if (action === "nft") {
      toUseNftToClaimView();
    }
  }

  function openMocaverseProfilePage(): void {
    window.open(`${config.mocaverseURL}/user-board`, "_blank");
  }

  function openTxOnPolygonScan(): void {
    console.log("openTxOnPolygonScan", mocaSignature.value);
    window.open(`${config.polygonBlockExplorerUrl}/tx/${mocaSignature.value}`, "_blank");
  }

  function openContractWalletOnPolygonScan(): void {
    window.open(`${config.polygonBlockExplorerUrl}/address/${useController.contractWalletAddress}`, "_blank");
  }

  /// * Change state for UI
  /// Select Claim Action
  function toSelectClaimActionView(): void {
    leftSide.value = "WhatIsMoca";
    rightSide.value = "ChooseClaimMethod";
  }

  /// Use NFT to claim
  async function toUseNftToClaimView(): Promise<void> {
    leftSide.value = "WhatIsMoca";
    rightSide.value = "CheckMoca";
  }

  /// Use Invite Code to claim
  function toUseInviteCodeToClaimView(): void {
    leftSide.value = "WhatIsMoca";
    rightSide.value = "CheckInviteCode";
  }

  /// Ask Use to Link with Partner
  function toLinkPartnerView(): void {
    leftSide.value = "WhatIsMoca";
    rightSide.value = "LinkPartner";
  }

  /// Parnter is Full
  function toPartnerQuotaIsFullView(): void {
    leftSide.value = "WhatIsMoca";
    rightSide.value = "ClaimQuotaFull";
  }

  /// Claim Moca ID
  function toClaimMocaIDView(): void {
    leftSide.value = "WhyMoca";
    rightSide.value = "ClaimMocaID";
  }

  /// Claim Moca ID Success
  function toClaimMocaIDSuccessView(): void {
    leftSide.value = "ExploreMocaverse";
    rightSide.value = "ClaimMocaIDSuccess";
  }

  /// Reset ErrorMessage
  function resetErrorMessage(): void {
    errorText.value = "";
  }

  function reset(): void {
    Cookies.remove("web3auth_jwt", {
      domain: ".mocaverse.xyz",
    });
    if (storageAvailable("sessionStorage")) {
      sessionStorage.removeItem(EMAIL_CONSENT_SESSION_KEY);
      sessionStorage.removeItem(MOCA_ID_JWT_KEY);
      sessionStorage.removeItem(MOCA_ID_PARTNER_JWT_KEY);
    }
    token = "";
    partnerJwt = "";
    emailConsent.value = true;
    leftSide.value = "WhatIsMoca";
    rightSide.value = "CreateWallet";
    inviteCode.value = "";
    mocaId.value = "";
  }

  return {
    leftSide,
    rightSide,
    isLoading,
    errorText,
    emailConsent,
    inviteCode,
    mocaId,
    partner,
    userInPartner,
    user,
    mocaNFTAddress,
    mocaSignature,
    checkEligibility,
    showClaimMethods: toSelectClaimActionView,
    chooseClaimMethod,
    verifyInviteCode,
    verifyContainEligibleNFT,
    reserveName,
    addPartnerDetails,
    skipAddPartnerDetails: toClaimMocaIDSuccessView,
    openMocaverseProfilePage,
    openContractWalletOnPolygonScan,
    openTxOnPolygonScan,
    resetErrorMessage,
    reset,
  };
});
